import React, { useEffect, useState } from 'react';
import {
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardFooter,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
  Stack,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import {
  FaAddressBook,
  FaMapMarkerAlt,
  FiMail,
  FiPhone,
} from 'react-icons/all';
import { setSite } from '../../../features/site/siteSlice';
import { useDispatch, useSelector } from 'react-redux';
import { updateSite, getUser } from '../../../api/siteApi';

function ContactUs({ isEdit, prevClicked, nextClicked }) {
  const { user } = useSelector(state => state.user);
  const { site } = useSelector(state => state.site);
  const dispatch = useDispatch();
  const [address, setAddress] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [mapUrl, setMapUrl] = useState('');
  const [inProgress, setInProgress] = useState(false);
  const [displayNext, setDisplayNext] = useState(isEdit);
  const [actionStatus, setActionStatus] = useState('');
  const toast = useToast();

  useEffect(() => {
    const initializeData = async () => {
      let defaultPhone = '';
      let defaultEmail = '';
      let defaultAddress = '';

      if (site && site.siteOwner) {
        try {
          console.log(site);
          const res = await getUser(site.siteOwner);
          if (res.user) {
            defaultPhone = res.user.phone || '';
            defaultEmail = res.user.email || '';
            defaultAddress = res.user.address || '';
          }
        } catch (error) {
          console.error('Error fetching site owner details:', error);
        }
      } else if (user) {
        defaultPhone = user.phone || '';
        defaultEmail = user.email || '';
        defaultAddress = user.address || '';
      }

      setPhone(site?.contactUs?.phone || defaultPhone);
      setEmail(site?.contactUs?.email || defaultEmail);
      setAddress(site?.contactUs?.address || defaultAddress);
      setMapUrl(site?.contactUs?.mapUrl || '');
    };

    initializeData();
    // eslint-disable-next-line
  }, []);

  const extractAndValidateMapUrl = input => {
    if (!input || !input.trim()) return '';
    let url = input.trim();

    // Extract src from iframe HTML snippet if pasted
    const srcMatch = url.match(/src=["']([^"']+)["']/i);
    if (srcMatch && srcMatch[1]) {
      url = srcMatch[1];
    }

    try {
      const parsedUrl = new URL(url);
      if (parsedUrl.protocol !== 'https:') {
        return null;
      }
      const host = parsedUrl.hostname.toLowerCase();
      const isGoogleMap =
        host.includes('google') &&
        (parsedUrl.pathname.includes('/maps') ||
          parsedUrl.pathname.includes('/embed'));

      if (!isGoogleMap) {
        return null;
      }
      return url;
    } catch (e) {
      return null;
    }
  };

  const addContactDetails = () => {
    if (phone === '' || email === '' || address === '') {
      toast({
        title: 'Error',
        description: 'Please set the proper details',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
      return;
    }

    let processedMapUrl = '';
    if (mapUrl && mapUrl.trim() !== '') {
      const validated = extractAndValidateMapUrl(mapUrl);
      if (validated === null) {
        toast({
          title: 'Invalid Map URL',
          description:
            'Please enter a valid Google Maps embed link or iframe HTML code.',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        });
        return;
      }
      processedMapUrl = validated;
    }

    setInProgress(true);
    const payload = {
      phone,
      email,
      address,
      mapUrl: processedMapUrl,
      id: site._id,
      creator: user._id,
      updateType: 'CONTACT_US',
    };
    updateSite(payload)
      .then(response => {
        setInProgress(false);
        if (response.site) {
          if (isEdit) {
            dispatch(setSite(response.site));
          }
          let message = isEdit
            ? 'Contact Details updated successfully.'
            : 'Contact Details added successfully, Please add the follow-up details.';
          toast({
            title: 'Success',
            description: message,
            status: 'success',
            duration: 5000,
            isClosable: true,
            position: 'top-right',
          });
          setActionStatus(`${message}`);
          setDisplayNext(true);
        } else {
          toast({
            title: 'Error',
            description: response.message,
            status: 'error',
            duration: 5000,
            isClosable: true,
            position: 'top-right',
          });
        }
      })
      .catch(error => {
        setInProgress(false);
        toast({
          title: 'Error',
          description: 'Something went wrong, please try again',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        });
      });
  };

  return (
    <Card bg={useColorModeValue('white', 'gray.700')}>
      <CardBody>
        <Stack spacing="4" mt="2">
          <FormControl isRequired>
            <FormLabel>Location</FormLabel>
            <InputGroup>
              <InputLeftElement
                pointerEvents="none"
                children={<FaAddressBook color="gray.500" />}
              />
              <Input
                type="text"
                name="Address"
                placeholder="Enter Address"
                defaultValue={address}
                onChange={event => setAddress(event.target.value)}
              />
            </InputGroup>
          </FormControl>
          <FormControl isRequired>
            <FormLabel>Email</FormLabel>
            <InputGroup>
              <InputLeftElement
                pointerEvents="none"
                children={<FiMail color="gray.500" />}
              />
              <Input
                type="email"
                name="Email"
                placeholder="Enter Email Address"
                defaultValue={email}
                onChange={event => setEmail(event.target.value)}
              />
            </InputGroup>
          </FormControl>
          <FormControl isRequired>
            <FormLabel>Phone</FormLabel>
            <InputGroup>
              <InputLeftElement
                pointerEvents="none"
                children={<FiPhone color="gray.500" />}
              />
              <Input
                type="number"
                name="Phone number"
                placeholder="Enter Phone Number"
                defaultValue={phone}
                onChange={event => setPhone(event.target.value)}
              />
            </InputGroup>
          </FormControl>
          <FormControl>
            <FormLabel>Google Maps Embed Link</FormLabel>
            <InputGroup>
              <InputLeftElement
                pointerEvents="none"
                children={<FaMapMarkerAlt color="gray.500" />}
              />
              <Input
                type="text"
                name="Map URL"
                placeholder="Paste Google Maps embed code (<iframe...>) or embed link"
                value={mapUrl}
                onChange={event => setMapUrl(event.target.value)}
              />
            </InputGroup>
            <FormHelperText>
              To get this: Go to Google Maps → Share → Embed a map → Copy HTML
              (or copy the embed URL).
            </FormHelperText>
          </FormControl>
        </Stack>
      </CardBody>
      <CardFooter>
        <ButtonGroup w={'100%'} justifyContent="end">
          <Button colorScheme="teal" size="md" onClick={prevClicked}>
            Previous
          </Button>
          <Button
            colorScheme="teal"
            isLoading={inProgress}
            loadingText="Submitting..."
            disabled={inProgress || actionStatus}
            size="md"
            onClick={addContactDetails}
            whiteSpace="normal"
          >
            {isEdit ? 'Update' : 'Add'}
          </Button>
          <Button
            colorScheme="teal"
            size="md"
            style={{ display: displayNext ? 'inherit' : 'none' }}
            onClick={nextClicked}
          >
            Next
          </Button>
        </ButtonGroup>
      </CardFooter>
    </Card>
  );
}

export default ContactUs;
